import { useState, useEffect, useRef, useCallback } from 'react';
import { Cloud, CheckCircle, AlertCircle, Settings, Eye, EyeOff, X } from 'lucide-react';
import { Modal } from './Modal';

// Toast notification types
interface Toast {
  id: string;
  message: string;
  type: 'error' | 'success' | 'info';
  targetRect?: DOMRect; // Optional: position relative to an element
}

interface PlatformIntegration {
  id: string;
  name: string;
  description: string;
  category: 'nutrition' | 'fitness' | 'cloud' | 'database';
  status: 'available' | 'connected' | 'disabled';
  icon: string;
  provider: string;
  purpose: string;
  tags: string[];
}

interface ConnectionStatus {
  connected: boolean;
  platformInfo?: {
    consumerKey?: string;
    configuredAt?: string;
  };
}

export function AdminIntegrations() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [integrations, setIntegrations] = useState<PlatformIntegration[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<Record<string, ConnectionStatus>>({});
  const [loading, setLoading] = useState(true);
  const [flippedCards, setFlippedCards] = useState<Set<string>>(new Set());
  const [configForm, setConfigForm] = useState({ consumerKey: '', consumerSecret: '' });
  const [showSecret, setShowSecret] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [pendingDisconnect, setPendingDisconnect] = useState<PlatformIntegration | null>(null);

  const categories = [
    { id: 'all', name: 'All Integrations', icon: '🔗' },
    { id: 'nutrition', name: 'Nutrition', icon: '🍎' },
    { id: 'fitness', name: 'Fitness', icon: '💪' },
    { id: 'cloud', name: 'Cloud Storage', icon: '☁️' },
    { id: 'database', name: 'Database', icon: '🗄️' }
  ];

  // Auth helper for API calls
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  // Toast notification helper
  const showToast = useCallback((message: string, type: 'error' | 'success' | 'info', integrationId?: string) => {
    const id = crypto.randomUUID();

    // Get target element rect if integrationId provided
    let targetRect: DOMRect | undefined;
    if (integrationId && cardRefs.current[integrationId]) {
      targetRect = cardRefs.current[integrationId]?.getBoundingClientRect();
    }

    const toast: Toast = { id, message, type, targetRect };
    setToasts(prev => [...prev, toast]);

    // Auto-dismiss after 4 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const availableIntegrations: PlatformIntegration[] = [
    // Nutrition APIs
    {
      id: 'fatsecret',
      name: 'FatSecret Platform',
      description: 'Nutrition database API for food tracking, calorie counting, and dietary analysis. Connects client food diaries to the platform.',
      category: 'nutrition',
      status: 'available',
      icon: '🍎',
      provider: 'FatSecret Platform API',
      purpose: 'Nutrition Tracking',
      tags: ['OAuth 1.0', 'Nutrition DB', 'Food Diary']
    },
    // Fitness APIs (Future)
    {
      id: 'googlefit',
      name: 'Google Fit',
      description: 'Activity tracking, step counting, and workout data from Android devices and Wear OS.',
      category: 'fitness',
      status: 'disabled',
      icon: '🏃',
      provider: 'Google LLC',
      purpose: 'Activity Tracking',
      tags: ['OAuth 2.0', 'Steps', 'Workouts']
    },
    {
      id: 'applehealth',
      name: 'Apple Health',
      description: 'Health and fitness data from iPhone, Apple Watch, and compatible apps.',
      category: 'fitness',
      status: 'disabled',
      icon: '❤️',
      provider: 'Apple Inc.',
      purpose: 'Health Data',
      tags: ['HealthKit', 'iOS Only', 'Workouts']
    },
    // Cloud Storage (Future)
    {
      id: 'dropbox',
      name: 'Dropbox',
      description: 'Cloud backup storage for automated platform backups and data export.',
      category: 'cloud',
      status: 'disabled',
      icon: '📦',
      provider: 'Dropbox Inc.',
      purpose: 'Backup Storage',
      tags: ['OAuth 2.0', 'Auto-Backup', 'Export']
    },
    {
      id: 'googledrive',
      name: 'Google Drive',
      description: 'Cloud storage integration for backup redundancy with 15GB free tier.',
      category: 'cloud',
      status: 'disabled',
      icon: '📁',
      provider: 'Google LLC',
      purpose: 'Backup Storage',
      tags: ['OAuth 2.0', '15GB Free', 'Export']
    },
    // Database (Future)
    {
      id: 'postgresql',
      name: 'PostgreSQL',
      description: 'Production database migration from JSON file storage to PostgreSQL.',
      category: 'database',
      status: 'disabled',
      icon: '🐘',
      provider: 'PostgreSQL Global Dev Group',
      purpose: 'Primary Database',
      tags: ['SQL', 'Scalable', 'ACID']
    }
  ];

  useEffect(() => {
    loadIntegrationStatus();
  }, []);

  const loadIntegrationStatus = async () => {
    try {
      setIntegrations(availableIntegrations);
      const status: Record<string, ConnectionStatus> = {};

      // Check FatSecret platform status
      try {
        const response = await fetch('/api/fatsecret/platform/status', {
          headers: getAuthHeaders()
        });
        const data = await response.json();
        if (data.success) {
          status.fatsecret = {
            connected: data.configured,
            platformInfo: data.platformInfo
          };
        }
      } catch (error) {
        console.warn('Failed to load FatSecret platform status:', error);
      }

      setConnectionStatus(status);

      // Update integration statuses based on connection status
      const updatedIntegrations = availableIntegrations.map(integration => ({
        ...integration,
        status: status[integration.id]?.connected ? 'connected' : integration.status
      }));

      setIntegrations(updatedIntegrations as PlatformIntegration[]);
    } catch (error) {
      console.error('Failed to load integration status:', error);
      setIntegrations(availableIntegrations);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = (integration: PlatformIntegration) => {
    if (integration.status === 'disabled') {
      return;
    }

    if (integration.id === 'fatsecret') {
      // Flip the tile to show configuration form
      const newFlippedCards = new Set(flippedCards);
      newFlippedCards.add(integration.id);
      setFlippedCards(newFlippedCards);
      setConfigForm({ consumerKey: '', consumerSecret: '' });
    }
  };

  const handleDisconnect = (integration: PlatformIntegration) => {
    setPendingDisconnect(integration);
    setShowDisconnectModal(true);
  };

  const confirmDisconnect = async () => {
    if (!pendingDisconnect) return;

    try {
      const response = await fetch('/api/fatsecret/platform/disconnect', {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      if (data.success) {
        await loadIntegrationStatus();
        setSuccessMessage(`Successfully disconnected ${pendingDisconnect.name}. Platform credentials have been removed.`);
        setShowSuccessModal(true);
      } else {
        alert(`Failed to disconnect: ${data.message}`);
      }
    } catch (error) {
      console.error('Disconnect error:', error);
      alert('Failed to disconnect. Please try again.');
    } finally {
      setShowDisconnectModal(false);
      setPendingDisconnect(null);
    }
  };

  const handleFlipCancel = (integrationId: string) => {
    const newFlippedCards = new Set(flippedCards);
    newFlippedCards.delete(integrationId);
    setFlippedCards(newFlippedCards);
    setConfigForm({ consumerKey: '', consumerSecret: '' });
    setShowSecret(false);
  };

  const handleFlipSubmit = async (integrationId: string) => {
    if (integrationId !== 'fatsecret') return;

    const { consumerKey, consumerSecret } = configForm;

    if (!consumerKey?.trim() || !consumerSecret?.trim()) {
      showToast('Both Consumer Key and Consumer Secret are required', 'error', integrationId);
      return;
    }

    try {
      setSubmitting(true);

      const response = await fetch('/api/fatsecret/admin/configure', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          consumerKey: consumerKey.trim(),
          consumerSecret: consumerSecret.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        // Close the flipped card
        const newFlippedCards = new Set(flippedCards);
        newFlippedCards.delete(integrationId);
        setFlippedCards(newFlippedCards);
        setConfigForm({ consumerKey: '', consumerSecret: '' });
        setShowSecret(false);

        // Show success and refresh status
        setSuccessMessage(`FatSecret Platform connected successfully!\n\nClients can now connect their FatSecret accounts.`);
        setShowSuccessModal(true);

        await loadIntegrationStatus();
      } else {
        // Show error toast over the card
        showToast(data.message || 'Failed to validate credentials', 'error', integrationId);
      }
    } catch (error) {
      console.error('Configuration error:', error);
      showToast('Failed to connect. Please check your credentials and try again.', 'error', integrationId);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredIntegrations = selectedCategory === 'all'
    ? integrations
    : integrations.filter(integration => integration.category === selectedCategory);

  const getStatusIcon = (status: string, connected?: boolean) => {
    if (connected) return <CheckCircle className="w-5 h-5 text-green-500" />;
    if (status === 'disabled') return <AlertCircle className="w-5 h-5" style={{ color: 'var(--text-tertiary)' }} />;
    return <Cloud className="w-5 h-5" style={{ color: 'var(--accent-blue)' }} />;
  };

  if (loading) {
    return (
      <div className="admin-integrations">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading platform integrations...</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="admin-integrations">
      {/* Header */}
      <div className="integrations-header">
        <h1>Platform Integrations</h1>
        <p>Configure third-party services and APIs for Mission Control</p>
      </div>

      {/* Category Filter */}
      <div className="category-filters">
        {categories.map(category => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
          >
            <span className="category-icon">{category.icon}</span>
            {category.name}
          </button>
        ))}
      </div>

      {/* Integration Grid */}
      <div className="integrations-grid">
        {filteredIntegrations.map(integration => {
          const connected = connectionStatus[integration.id]?.connected;
          const isFlipped = flippedCards.has(integration.id);

          return (
            <div
              key={integration.id}
              className="card-container"
              ref={el => cardRefs.current[integration.id] = el}
            >
              <div className={`integration-card ${connected ? 'connected' : ''} ${isFlipped ? 'flipped' : ''}`}>

                {/* Front Face */}
                <div className="card-face card-front">
                  <div className="card-header">
                    {/* Status Icon */}
                    <div className="status-icon">
                      {getStatusIcon(integration.status, connected)}
                    </div>

                    <div className="card-icon">{integration.icon}</div>

                    {/* Tags */}
                    <div className="card-tags">
                      {integration.tags.map((tag, idx) => (
                        <span key={idx} className="tag">{tag}</span>
                      ))}
                    </div>

                    <h3 className="card-title">{integration.name}</h3>
                    <p className="card-provider">{integration.provider}</p>
                    <span className="card-purpose">{integration.purpose}</span>
                  </div>

                  <div className="card-body">
                    <p className="card-description">{integration.description}</p>

                    <div className="card-actions">
                      {connected ? (
                        <div className="connected-actions">
                          <button
                            onClick={() => handleDisconnect(integration)}
                            className="btn-disconnect"
                          >
                            Disconnect
                          </button>
                          <button className="btn-settings" title="Settings">
                            <Settings className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleConnect(integration)}
                          disabled={integration.status === 'disabled'}
                          className="btn-connect"
                        >
                          {integration.status === 'disabled' ? 'Coming Soon' : 'Configure'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Back Face - Configuration Form */}
                <div className="card-face card-back">
                  {integration.id === 'fatsecret' && (
                    <div className="config-form">
                      <div className="config-header">
                        <h3>FatSecret Platform Setup</h3>
                        <p>Enter your OAuth 1.0 API credentials</p>
                      </div>

                      <div className="config-body">
                        <div className="form-group">
                          <label htmlFor={`${integration.id}-key`}>Consumer Key</label>
                          <input
                            id={`${integration.id}-key`}
                            type="text"
                            value={configForm.consumerKey}
                            onChange={(e) => setConfigForm({...configForm, consumerKey: e.target.value})}
                            placeholder="1a2d01cd17d84fba8d490cf380229f21"
                            className="config-input"
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor={`${integration.id}-secret`}>Consumer Secret</label>
                          <div className="secret-input-wrapper">
                            <input
                              id={`${integration.id}-secret`}
                              type={showSecret ? 'text' : 'password'}
                              value={configForm.consumerSecret}
                              onChange={(e) => setConfigForm({...configForm, consumerSecret: e.target.value})}
                              placeholder="135200dcbd77404eb0b88aa07956d582"
                              className="config-input"
                            />
                            <button
                              type="button"
                              className="toggle-visibility"
                              onClick={() => setShowSecret(!showSecret)}
                            >
                              {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>

                        <div className="config-actions">
                          <button
                            onClick={() => handleFlipCancel(integration.id)}
                            className="btn-cancel"
                            disabled={submitting}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleFlipSubmit(integration.id)}
                            className="btn-save"
                            disabled={!configForm.consumerKey?.trim() || !configForm.consumerSecret?.trim() || submitting}
                          >
                            {submitting ? 'Connecting...' : 'Save & Activate'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredIntegrations.length === 0 && (
        <div className="empty-state">
          <Cloud className="w-16 h-16" style={{ color: 'var(--text-tertiary)' }} />
          <h3>No integrations found</h3>
          <p>Try selecting a different category.</p>
        </div>
      )}

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Configuration Successful"
        variant="success"
        size="small"
        footer={
          <button
            className="btn btn-primary"
            onClick={() => setShowSuccessModal(false)}
          >
            Done
          </button>
        }
      >
        <p style={{ whiteSpace: 'pre-line' }}>{successMessage}</p>
      </Modal>

      {/* Disconnect Confirmation Modal */}
      <Modal
        isOpen={showDisconnectModal}
        onClose={() => {
          setShowDisconnectModal(false);
          setPendingDisconnect(null);
        }}
        title="Confirm Disconnection"
        variant="warning"
        size="small"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowDisconnectModal(false);
                setPendingDisconnect(null);
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={confirmDisconnect}
            >
              Disconnect
            </button>
          </>
        }
      >
        <p>Are you sure you want to disconnect <strong>{pendingDisconnect?.name}</strong>?</p>
        <p style={{ marginTop: '12px', color: 'var(--text-secondary)' }}>
          Your API credentials will be permanently removed. Clients will no longer be able to sync their {pendingDisconnect?.name} data until reconfigured.
        </p>
      </Modal>

      {/* Toast Notifications */}
      {toasts.map(toast => {
        // Calculate position - center over target element or default to viewport center
        const style: React.CSSProperties = {
          position: 'fixed',
          zIndex: 9999,
        };

        if (toast.targetRect) {
          // Position centered over the card
          style.left = toast.targetRect.left + (toast.targetRect.width / 2);
          style.top = toast.targetRect.top + (toast.targetRect.height / 2);
          style.transform = 'translate(-50%, -50%)';
        } else {
          // Default: bottom-right of viewport
          style.bottom = '24px';
          style.right = '24px';
        }

        return (
          <div
            key={toast.id}
            className={`toast toast-${toast.type}`}
            style={style}
          >
            <div className="toast-icon">
              {toast.type === 'error' && <AlertCircle className="w-5 h-5" />}
              {toast.type === 'success' && <CheckCircle className="w-5 h-5" />}
              {toast.type === 'info' && <Cloud className="w-5 h-5" />}
            </div>
            <span className="toast-message">{toast.message}</span>
            <button className="toast-dismiss" onClick={() => dismissToast(toast.id)}>
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .admin-integrations {
    max-width: 1400px;
    margin: 0 auto;
  }

  .integrations-header {
    margin-bottom: 32px;
  }

  .integrations-header h1 {
    font-size: 28px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 8px;
  }

  .integrations-header p {
    font-size: 16px;
    color: var(--text-secondary);
    margin: 0;
  }

  /* Loading State */
  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;
  }

  .loading-spinner {
    width: 48px;
    height: 48px;
    border: 3px solid var(--border-secondary);
    border-top-color: var(--accent-blue);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .loading-state p {
    margin-top: 16px;
    color: var(--text-secondary);
  }

  /* Category Filters */
  .category-filters {
    display: flex;
    gap: 12px;
    margin-bottom: 32px;
    flex-wrap: wrap;
  }

  .category-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    border-radius: 8px;
    border: 1px solid var(--border-secondary);
    background: var(--bg-primary);
    color: var(--text-secondary);
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .category-btn:hover:not(.active) {
    background: var(--bg-secondary);
    border-color: var(--border-primary);
  }

  .category-btn.active {
    background: var(--accent-blue);
    color: white;
    border-color: var(--accent-blue);
  }

  .category-icon {
    font-size: 16px;
  }

  /* Integration Grid */
  .integrations-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: 24px;
  }

  /* Card Container & Flip Animation */
  .card-container {
    perspective: 1000px;
    height: 380px;
  }

  .integration-card {
    position: relative;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .integration-card.flipped {
    transform: rotateY(180deg);
  }

  .card-face {
    position: absolute;
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    border-radius: 12px;
    background: var(--bg-primary);
    border: 1px solid var(--border-secondary);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
  }

  .card-front {
    transform: rotateY(0deg);
  }

  .card-back {
    transform: rotateY(180deg);
    padding: 24px;
  }

  /* Connected Card Styling */
  .integration-card.connected .card-face {
    border: 2px solid var(--accent-green);
  }

  /* Card Header */
  .card-header {
    padding: 20px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-secondary);
    position: relative;
  }

  .status-icon {
    position: absolute;
    top: 16px;
    right: 16px;
  }

  .card-icon {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, var(--accent-blue), #8B5CF6);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    margin-bottom: 12px;
  }

  .card-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 12px;
  }

  .tag {
    background: var(--bg-primary);
    color: var(--text-secondary);
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .card-title {
    font-size: 18px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 4px;
  }

  .card-provider {
    font-size: 13px;
    color: var(--text-tertiary);
    margin: 0 0 4px;
  }

  .card-purpose {
    font-size: 12px;
    color: var(--accent-blue);
    font-weight: 600;
  }

  /* Card Body */
  .card-body {
    padding: 20px;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .card-description {
    font-size: 14px;
    color: var(--text-secondary);
    line-height: 1.5;
    margin: 0 0 16px;
  }

  /* Card Actions */
  .card-actions {
    margin-top: auto;
    padding-top: 8px;
  }

  .connected-actions {
    display: flex;
    gap: 8px;
  }

  .btn-connect {
    width: 100%;
    padding: 12px 20px;
    border-radius: 8px;
    border: none;
    background: linear-gradient(135deg, var(--accent-blue), #8B5CF6);
    color: white;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }

  .btn-connect:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  .btn-connect:disabled {
    background: var(--bg-secondary);
    color: var(--text-tertiary);
    box-shadow: none;
    cursor: not-allowed;
  }

  .btn-disconnect {
    flex: 1;
    padding: 10px 16px;
    border-radius: 8px;
    border: 1px solid var(--accent-red);
    background: transparent;
    color: var(--accent-red);
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-disconnect:hover {
    background: var(--accent-red);
    color: white;
  }

  .btn-settings {
    padding: 10px;
    border-radius: 8px;
    border: 1px solid var(--border-secondary);
    background: var(--bg-secondary);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-settings:hover {
    background: var(--border-secondary);
    color: var(--text-primary);
  }

  /* Configuration Form (Back of Card) */
  .config-form {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .config-header {
    text-align: center;
    margin-bottom: 20px;
  }

  .config-header h3 {
    font-size: 18px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 8px;
  }

  .config-header p {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0;
  }

  .config-body {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .form-group label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 6px;
  }

  .config-input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--border-secondary);
    border-radius: 6px;
    background: var(--bg-secondary);
    color: var(--text-primary);
    font-size: 14px;
    transition: border-color 0.2s;
  }

  .config-input:focus {
    outline: none;
    border-color: var(--accent-blue);
  }

  .config-input::placeholder {
    color: var(--text-tertiary);
  }

  .secret-input-wrapper {
    position: relative;
  }

  .secret-input-wrapper .config-input {
    padding-right: 40px;
  }

  .toggle-visibility {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: var(--text-tertiary);
    cursor: pointer;
    padding: 4px;
  }

  .toggle-visibility:hover {
    color: var(--text-primary);
  }

  .config-actions {
    display: flex;
    gap: 12px;
    margin-top: auto;
    padding-top: 16px;
  }

  .btn-cancel {
    flex: 1;
    padding: 10px 16px;
    border: 1px solid var(--border-secondary);
    border-radius: 6px;
    background: var(--bg-secondary);
    color: var(--text-secondary);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-cancel:hover:not(:disabled) {
    background: var(--border-secondary);
    color: var(--text-primary);
  }

  .btn-save {
    flex: 1;
    padding: 10px 16px;
    border: none;
    border-radius: 6px;
    background: var(--accent-blue);
    color: white;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-save:hover:not(:disabled) {
    background: #2563eb;
  }

  .btn-save:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Toast Notifications */
  .toast {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: var(--bg-primary);
    border-radius: 10px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    max-width: 400px;
    animation: toastIn 0.3s ease-out;
  }

  @keyframes toastIn {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.9);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }

  .toast-error {
    border: 1px solid var(--accent-red);
  }

  .toast-error .toast-icon {
    color: var(--accent-red);
  }

  .toast-success {
    border: 1px solid var(--accent-green);
  }

  .toast-success .toast-icon {
    color: var(--accent-green);
  }

  .toast-info {
    border: 1px solid var(--accent-blue);
  }

  .toast-info .toast-icon {
    color: var(--accent-blue);
  }

  .toast-icon {
    flex-shrink: 0;
  }

  .toast-message {
    flex: 1;
    font-size: 14px;
    color: var(--text-primary);
    line-height: 1.4;
  }

  .toast-dismiss {
    flex-shrink: 0;
    background: none;
    border: none;
    color: var(--text-tertiary);
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: all 0.2s;
  }

  .toast-dismiss:hover {
    color: var(--text-primary);
    background: var(--bg-secondary);
  }

  /* Empty State */
  .empty-state {
    text-align: center;
    padding: 80px 20px;
  }

  .empty-state h3 {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 16px 0 8px;
  }

  .empty-state p {
    color: var(--text-secondary);
  }

  /* Modal button styles */
  .btn {
    padding: 10px 20px;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-primary {
    background: var(--accent-blue);
    color: white;
    border: none;
  }

  .btn-primary:hover {
    background: #2563eb;
  }

  .btn-secondary {
    background: var(--bg-secondary);
    color: var(--text-secondary);
    border: 1px solid var(--border-secondary);
  }

  .btn-secondary:hover {
    background: var(--border-secondary);
    color: var(--text-primary);
  }

  .btn-danger {
    background: var(--accent-red);
    color: white;
    border: none;
  }

  .btn-danger:hover {
    opacity: 0.9;
  }
`;

export default AdminIntegrations;
